/**
 * Rest-pose reorientation — the C·B law (ADR-003 §11, ADR-005 §3), the
 * kernel rewrite of the ACCEPTED Sol proposal
 * (docs/research/proposals/rest-pose-reorientation/, 50/50 verification).
 *
 * The naive frame map R = srcBind⁻¹·tgtBind is correct only when the two
 * rigs' local frames already agree semantically. This stage transports the
 * target child direction through that bind prior B and applies only the
 * shortest-arc correction C the observed child directions prove: F = C·B.
 * It replaces v2's empirical threshold ladder (0.996 / −0.9 / 0.60 / 30°)
 * with a continuous rule; the remaining thresholds are DERIVED — the
 * antipodal zone from float resolution widened by the inputs' measurement
 * uncertainty (tolerance/child-length per side), and the secondary-evidence
 * floors from the same angular uncertainty — never tuned on rig corpora.
 *
 * Pipeline semantics (deltas from the research reference, by design):
 * - The AUTHORED-FRAME tier outranks ALL measured evidence (authored >
 *   measured > bilateral-child > declared-frame > bind-prior): a validated
 *   Hand Studio joint-frame override (ADR-010 §11, PR #24) is a human/tool
 *   DECLARATION of a bone's local frame — the hand wins over automation. It
 *   fires when EITHER side carries an authored primary+secondary pair (unit +
 *   ⊥ gated at priors load); the non-authored side falls back to its best
 *   measured/declared evidence, hybrid per side like the declared-frame tier.
 * - Where child-direction evidence is unavailable (clavicle class, missing
 *   or co-located children), the result DEGRADES to the bind prior — the
 *   pre-§11 behavior — with the degradation named and reported, never
 *   silent. The bind prior is today's floor, not a guess.
 * - Where the evidence PROVES the bind prior wrong but cannot fix it (exact
 *   anti-parallel with no usable secondary), the result is a typed
 *   `undetermined` — the pipeline fails loud rather than ship 180°-mangled
 *   motion.
 * - Non-finite inputs throw before any classification (absorbed Codex
 *   finding, PR #6: a NaN must never read as "zero-length child").
 *
 * Depends on: math/quat, math/vec3, ir/errors, ./frame-map.
 */
import {
  quatFromAxisAngle,
  quatInvertUnit,
  quatMultiply,
  quatNormalize,
  quatRotateVec3,
  type Quat,
} from '../math/quat.js';
import {
  vec3Add,
  vec3Cross,
  vec3Dot,
  vec3Length,
  vec3Normalize,
  vec3Scale,
  vec3Sub,
  type Vec3Data,
} from '../math/vec3.js';
import { KernelError } from '../ir/errors.js';
import { properFrameMap, type FrameMap } from './frame-map.js';

export class InvalidReorientationInputError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(
      `Reorientation input is invalid (${issues.length} issue(s)):\n` +
        issues.map((i) => `  - ${i}`).join('\n'),
    );
  }
}

/** Thrown by the pipeline when a bone's frame map is provably ambiguous. */
export class ReorientationUndeterminedError extends KernelError {
  constructor(
    readonly boneName: string,
    readonly reason: ReorientUndetermined['reason'],
    readonly needs: string,
  ) {
    super(
      `Reorientation of bone "${boneName}" is undetermined (${reason}): ` +
        `the bind prior is provably wrong here and the inputs cannot fix it. Needs ${needs}.`,
    );
  }
}

export interface ReorientBoneSide {
  /** Bone-local bind rotation. */
  readonly localBind: Quat;
  /** World bind (from inverse-bind matrices — ADR-003 §2b). */
  readonly worldBind: { readonly position: Vec3Data; readonly rotation: Quat };
  /** World bind position of the ontology primaryChild's rig bone, if mapped. */
  readonly childWorldBindPosition: Vec3Data | null;
  /** World bind position of the primary grandchild's rig bone, if mapped. */
  readonly grandchildWorldBindPosition: Vec3Data | null;
  /**
   * The ontology's zeroPoseSecondary (§11) expressed in THIS bone's local
   * frame — declared twist-reference evidence for when grandchild geometry
   * cannot witness the twist DOF (straight/collinear chains). Computed by
   * the pipeline from the hub axes + the rig's declared up/facing; it
   * assumes the bind pose is authored in that declared world convention, so
   * it ranks below measured grandchild directions. Null when the ontology
   * has no secondary for the bone or the rig's convention is degenerate.
   */
  readonly canonicalSecondaryLocal: Vec3Data | null;
  /**
   * The ontology's zeroPoseDirection (§11) expressed in THIS bone's local
   * frame — the declared PRIMARY, for when the measured child direction
   * itself is unavailable: co-located axial clusters (the goblin's
   * Hip/Pelvis/Waist stack left Hips' child offset under tolerance — the
   * scissor-arc hip forensics, 2026-07-12), missing children, and the
   * declared child-direction-uninformative class (clavicles). Same
   * provenance caveat as the canonical secondary; null when unavailable.
   */
  readonly declaredPrimaryLocal: Vec3Data | null;
  /**
   * World-bind positions of the bone's bilateral mirror-pair children —
   * `[left, right]`, e.g. Hips' LeftUpLeg/RightUpLeg rig bones. Their
   * difference is the lateral (side) axis of a co-located CENTER bone whose own
   * child offset is starved (the Hip/Pelvis/Waist stack): the anatomically-wide,
   * well-conditioned leg spread evidences the twist frame the zero-length
   * primary cannot — a MEASURED per-rig frame that outranks the hub-idealized
   * declared frame (design note: REORIENT_BILATERAL_CHILD_FRAME). null when the
   * bone has no ontology mirror-pair of children, or either child is unmapped.
   */
  readonly bilateralChildPositions: readonly [Vec3Data, Vec3Data] | null;
  /** Authored joint-frame override for THIS side's (family, bone) — the Hand
   *  Studio frame contract (PR #24, ADR-010 §11). Bone-local unit primary;
   *  validated unit + ⊥ at priors load. Null when the family has no entry. */
  readonly authoredPrimaryLocal: Vec3Data | null;
  readonly authoredSecondaryLocal: Vec3Data | null;
}

export interface ReorientInput {
  readonly source: ReorientBoneSide;
  readonly target: ReorientBoneSide;
  /** From the ontology (§11): false for the clavicle class. */
  readonly childDirectionInformative: boolean;
  /** Co-location floor for child offsets, in each rig's own units. */
  readonly sourceTolerance: number;
  readonly targetTolerance: number;
}

export type ReorientEvidence =
  | 'authored-frame'
  | 'two-directions'
  | 'canonical-secondary'
  | 'bilateral-child'
  | 'declared-frame'
  | 'primary-plus-bind-prior'
  | 'antipodal-bind-axis'
  | 'bind-prior-only';

export type ReorientDegradation =
  | 'child-direction-uninformative'
  | 'missing-child'
  | 'zero-length-child';

export interface ReorientFrameMap {
  readonly kind: 'frameMap';
  readonly frameMap: FrameMap;
  readonly confidence: 'high' | 'medium' | 'low';
  readonly evidence: ReorientEvidence;
  /** Named when the child-direction evidence path was unavailable. */
  readonly degradedBecause: ReorientDegradation | null;
  /** Angle the bind prior missed by; null when nothing was measured. */
  readonly residualRadians: number | null;
}

export interface ReorientUndetermined {
  readonly kind: 'undetermined';
  readonly reason: 'antiparallel-axis-ambiguous' | 'collinear-secondary';
  readonly needs: string;
}

export type ReorientResult = ReorientFrameMap | ReorientUndetermined;

/** The machine-resolution FLOOR of the antipodal zone (the accepted
 * treatment's 64ε); the effective zone widens with the directions' measured
 * angular uncertainty — see computeReorientation. Never tuned on rigs. */
const ANTIPODAL_ZONE = 64 * Number.EPSILON;
/** Projected secondaries shorter than this (on unit inputs) carry no twist. */
const SECONDARY_MIN = 1e-10;

function clampDot(d: number): number {
  return Math.max(-1, Math.min(1, d));
}

function finiteVec(v: Vec3Data): boolean {
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}

function finiteQuat(q: Quat): boolean {
  return (
    Number.isFinite(q.x) && Number.isFinite(q.y) && Number.isFinite(q.z) && Number.isFinite(q.w)
  );
}

function validate(input: ReorientInput): void {
  const issues: string[] = [];
  for (const [label, side] of [
    ['source', input.source],
    ['target', input.target],
  ] as const) {
    if (!finiteQuat(side.localBind)) issues.push(`${label}.localBind is not finite`);
    if (!finiteQuat(side.worldBind.rotation)) issues.push(`${label}.worldBind.rotation is not finite`);
    if (!finiteVec(side.worldBind.position)) issues.push(`${label}.worldBind.position is not finite`);
    if (side.childWorldBindPosition !== null && !finiteVec(side.childWorldBindPosition)) {
      issues.push(`${label}.childWorldBindPosition is not finite`);
    }
    if (side.grandchildWorldBindPosition !== null && !finiteVec(side.grandchildWorldBindPosition)) {
      issues.push(`${label}.grandchildWorldBindPosition is not finite`);
    }
    if (side.canonicalSecondaryLocal !== null && !finiteVec(side.canonicalSecondaryLocal)) {
      issues.push(`${label}.canonicalSecondaryLocal is not finite`);
    }
    if (side.declaredPrimaryLocal !== null && !finiteVec(side.declaredPrimaryLocal)) {
      issues.push(`${label}.declaredPrimaryLocal is not finite`);
    }
    if (side.bilateralChildPositions !== null) {
      const [a, b] = side.bilateralChildPositions;
      if (!finiteVec(a) || !finiteVec(b)) issues.push(`${label}.bilateralChildPositions is not finite`);
    }
    if (side.authoredPrimaryLocal !== null && !finiteVec(side.authoredPrimaryLocal)) {
      issues.push(`${label}.authoredPrimaryLocal is not finite`);
    }
    if (side.authoredSecondaryLocal !== null && !finiteVec(side.authoredSecondaryLocal)) {
      issues.push(`${label}.authoredSecondaryLocal is not finite`);
    }
  }
  if (!Number.isFinite(input.sourceTolerance) || input.sourceTolerance <= 0) {
    issues.push('sourceTolerance must be a finite positive number');
  }
  if (!Number.isFinite(input.targetTolerance) || input.targetTolerance <= 0) {
    issues.push('targetTolerance must be a finite positive number');
  }
  if (issues.length > 0) throw new InvalidReorientationInputError(issues);
}

/** Direction from the bone to a world point, expressed in the bone's local frame. */
function localDirection(side: ReorientBoneSide, worldPoint: Vec3Data): Vec3Data {
  const offset = vec3Sub(worldPoint, side.worldBind.position);
  return vec3Normalize(
    quatRotateVec3(quatInvertUnit(quatNormalize(side.worldBind.rotation)), offset),
  );
}

/**
 * Grandchild direction projected perpendicular to the primary — the twist
 * witness. A grandchild co-located with the bone (or separated only by
 * sub-tolerance noise) is NO evidence: normalizing it would either throw or
 * promote noise to high-confidence twist (Codex review, PR #8). The
 * perpendicular remainder |p| = sin∠(g, primary) is formed from BOTH
 * measured directions, so it must clear the SUM of their angular
 * uncertainties — the grandchild's own (tolerance/‖offset‖) plus the
 * primary's (tolerance/childLength): a short noisy primary poisons the
 * projection no matter how long and accurate the grandchild is (review
 * findings, PR #8).
 */
function projectedSecondary(
  side: ReorientBoneSide,
  primary: Vec3Data,
  tolerance: number,
  primaryAngularNoise: number,
): Vec3Data | null {
  if (side.grandchildWorldBindPosition === null) return null;
  const offset = vec3Sub(side.grandchildWorldBindPosition, side.worldBind.position);
  const offsetLength = vec3Length(offset);
  if (offsetLength <= tolerance) return null;
  const g = localDirection(side, side.grandchildWorldBindPosition);
  const p = vec3Sub(g, vec3Scale(primary, vec3Dot(g, primary)));
  const floor = Math.max(SECONDARY_MIN, tolerance / offsetLength + primaryAngularNoise);
  return vec3Length(p) > floor ? vec3Normalize(p) : null;
}

/**
 * The declared canonical secondary (§11) projected perpendicular to the
 * MEASURED primary. The declared axis is noise-free, but the primary it is
 * projected against carries the child's angular uncertainty — a canonical
 * axis within that cone of the primary is unusable.
 */
function projectedCanonical(
  side: ReorientBoneSide,
  primary: Vec3Data,
  primaryAngularNoise: number,
): Vec3Data | null {
  if (side.canonicalSecondaryLocal === null) return null;
  const c = side.canonicalSecondaryLocal;
  if (vec3Length(c) <= SECONDARY_MIN) return null;
  const u = vec3Normalize(c);
  const p = vec3Sub(u, vec3Scale(primary, vec3Dot(u, primary)));
  const floor = Math.max(SECONDARY_MIN, primaryAngularNoise);
  return vec3Length(p) > floor ? vec3Normalize(p) : null;
}

/**
 * Stable shortest-arc quaternion from → to; null inside the antipodal zone.
 * The zone is derived per call from the directions' measurement uncertainty
 * (with the machine-ε floor as the lower bound) — see computeReorientation.
 */
function shortestArc(from: Vec3Data, to: Vec3Data, zone: number): Quat | null {
  const d = clampDot(vec3Dot(from, to));
  if (1 + d <= zone) return null;
  const c = vec3Cross(from, to);
  // ‖(c, 1+d)‖² = 2(1+d): well-conditioned everywhere outside the zone.
  return quatNormalize({ x: c.x, y: c.y, z: c.z, w: 1 + d });
}

/**
 * Compute the proper frame map for one mapped bone pair via the C·B law.
 * See the module header for the degradation/refusal semantics.
 */
export function computeReorientation(input: ReorientInput): ReorientResult {
  validate(input);
  const srcBind = quatNormalize(input.source.localBind);
  const tgtBind = quatNormalize(input.target.localBind);
  const bindPrior = quatNormalize(quatMultiply(quatInvertUnit(srcBind), tgtBind));

  const degraded = (why: ReorientDegradation): ReorientFrameMap => ({
    kind: 'frameMap',
    frameMap: properFrameMap(srcBind, tgtBind),
    confidence: 'low',
    evidence: 'bind-prior-only',
    degradedBecause: why,
    residualRadians: null,
  });

  /**
   * Per-side measured primary: the child direction, when the child exists
   * and its offset clears the co-location tolerance. Null = this side has
   * no measured primary and must borrow the declared one.
   */
  const measuredPrimary = (
    side: ReorientBoneSide,
    tolerance: number,
  ): { dir: Vec3Data; noise: number } | null => {
    if (side.childWorldBindPosition === null) return null;
    const offset = vec3Sub(side.childWorldBindPosition, side.worldBind.position);
    const length = vec3Length(offset);
    if (length <= tolerance) return null;
    return { dir: localDirection(side, side.childWorldBindPosition), noise: tolerance / length };
  };
  const mSrc = measuredPrimary(input.source, input.sourceTolerance);
  const mTgt = measuredPrimary(input.target, input.targetTolerance);

  /**
   * The shared frame-from-a-pair math, used by both the declared-frame and
   * bilateral-child tiers: given each side's primary direction (+ measurement
   * noise) and a ⊥ secondary, transport through the bind prior, correct the
   * primary by the shortest arc, then align the twist DOF from the secondaries.
   * Antipodal primaries take the evidence-derived π-flip about the secondaries'
   * bisector (never a hardcoded axis — the v2 Rz-vs-Rx scar). Returns null only
   * when that flip axis is itself degenerate.
   */
  const frameFromEvidence = (
    s: { dir: Vec3Data; noise: number },
    t: { dir: Vec3Data; noise: number },
    ss: Vec3Data,
    ts0: Vec3Data,
    evidence: ReorientEvidence,
    why: ReorientDegradation | null,
  ): ReorientFrameMap | null => {
    const transportedD = vec3Normalize(quatRotateVec3(bindPrior, t.dir));
    const pairNoise = s.noise + t.noise;
    const zone = Math.max(ANTIPODAL_ZONE, (pairNoise * pairNoise) / 2);
    const correction = shortestArc(transportedD, s.dir, zone);
    let rotation: Quat;
    if (correction !== null) {
      rotation = quatNormalize(quatMultiply(correction, bindPrior));
      const transportedSecondary = vec3Normalize(quatRotateVec3(rotation, ts0));
      const twist = Math.atan2(
        vec3Dot(s.dir, vec3Cross(transportedSecondary, ss)),
        clampDot(vec3Dot(transportedSecondary, ss)),
      );
      rotation = quatNormalize(quatMultiply(quatFromAxisAngle(s.dir, twist), rotation));
    } else {
      const ts = vec3Normalize(quatRotateVec3(bindPrior, ts0));
      let axis = vec3Add(ts, ss);
      if (vec3Length(axis) <= SECONDARY_MIN) axis = vec3Cross(transportedD, ts);
      axis = vec3Sub(axis, vec3Scale(transportedD, vec3Dot(axis, transportedD)));
      if (vec3Length(axis) <= SECONDARY_MIN) return null;
      rotation = quatNormalize(quatMultiply(quatFromAxisAngle(axis, Math.PI), bindPrior));
    }
    return {
      kind: 'frameMap',
      frameMap: { kind: 'proper', rotation },
      confidence: 'medium',
      evidence,
      degradedBecause: why,
      residualRadians: null,
    };
  };

  /**
   * Bilateral-child tier (design note: REORIENT_BILATERAL_CHILD_FRAME) — a
   * MEASURED frame for co-located CENTER bones (Hips), tried BEFORE the
   * hub-idealized declared frame because it is per-rig geometry, not an
   * idealization. When the immediate child is co-located (zero-length primary),
   * the frame is still fully evidenced by BIND geometry one level out: the
   * bilateral mirror-pair children (LeftUpLeg/RightUpLeg) give a wide, well-
   * conditioned lateral (twist) axis via their spread, and the first
   * non-co-located descendant down the primaryChild chain (the grandchild)
   * gives the pointing direction. HYBRID per side like declared-frame: the
   * measured child direction where valid, the grandchild-direction proxy only
   * where the primary is starved. Fires only where the bone carries a bilateral
   * child pair on BOTH sides; otherwise null → declared-frame floor.
   */
  const bilateralFrame = (
    why: ReorientDegradation,
    srcMeasured: { dir: Vec3Data; noise: number } | null,
    tgtMeasured: { dir: Vec3Data; noise: number } | null,
  ): ReorientFrameMap | null => {
    if (input.source.bilateralChildPositions === null || input.target.bilateralChildPositions === null) {
      return null;
    }
    // Per-side primary: measured child direction, else the grandchild direction
    // (a proxy for the pointing direction when the immediate child is co-located).
    const pick = (
      side: ReorientBoneSide,
      measured: { dir: Vec3Data; noise: number } | null,
      tolerance: number,
    ): { dir: Vec3Data; noise: number } | null => {
      if (measured !== null) return measured;
      if (side.grandchildWorldBindPosition === null) return null;
      const offset = vec3Sub(side.grandchildWorldBindPosition, side.worldBind.position);
      const length = vec3Length(offset);
      if (length <= tolerance) return null;
      return { dir: localDirection(side, side.grandchildWorldBindPosition), noise: tolerance / length };
    };
    const s = pick(input.source, srcMeasured, input.sourceTolerance);
    const t = pick(input.target, tgtMeasured, input.targetTolerance);
    if (s === null || t === null) return null;
    // Secondary: the bilateral child SPREAD (left − right = lateral axis),
    // carried into the bone-local frame and projected ⊥ the primary. It is a
    // world DIRECTION (not a point), rotated by the inverse world bind with no
    // position subtraction. Same anatomical sign on both sides (the ontology
    // mirror pair fixes left/right), so the twist references stay same-kind.
    const bilateralSecondary = (
      side: ReorientBoneSide,
      primary: Vec3Data,
      primaryNoise: number,
      tolerance: number,
    ): Vec3Data | null => {
      const [left, right] = side.bilateralChildPositions!;
      const worldSpread = vec3Sub(left, right);
      const spread = vec3Length(worldSpread);
      // The mirror children must clear the SAME co-location floor the measured
      // child/grandchild offsets use: a spread within importer jitter is not a
      // lateral axis, and would otherwise lock the twist frame to noise while
      // outranking declared-frame (Codex, PR #25).
      if (spread <= tolerance) return null;
      const localSpread = vec3Normalize(
        quatRotateVec3(quatInvertUnit(quatNormalize(side.worldBind.rotation)), worldSpread),
      );
      const p = vec3Sub(localSpread, vec3Scale(primary, vec3Dot(localSpread, primary)));
      // Projection floor carries the spread's own angular uncertainty
      // (tolerance/spread) plus the primary's — mirroring projectedSecondary.
      const floor = Math.max(SECONDARY_MIN, tolerance / spread + primaryNoise);
      return vec3Length(p) > floor ? vec3Normalize(p) : null;
    };
    const ss = bilateralSecondary(input.source, s.dir, s.noise, input.sourceTolerance);
    const ts0 = bilateralSecondary(input.target, t.dir, t.noise, input.targetTolerance);
    if (ss === null || ts0 === null) return null;
    return frameFromEvidence(s, t, ss, ts0, 'bilateral-child', why);
  };

  /**
   * Declared-frame tier: when a MEASURED primary is unavailable AND no
   * bilateral-child frame applies, a full frame is still evidenced from the
   * hub's declared pair (§11 zeroPoseDirection + zeroPoseSecondary, expressed
   * through the IBM-true world bind) — the raw bind prior on an axis-soup rig is
   * what turned the goblin's 12° pelvic yaw into a forward rock (co-located
   * axial cluster ⇒ zero-length-child ⇒ prior-only; forensics 2026-07-12).
   *
   * HYBRID per side (Codex, PR #23): each side contributes its BEST primary —
   * the measured child direction where valid, the declared hub direction only
   * where starved — so a one-sided co-location never discards the other side's
   * measured rest-pose evidence (the T↔A case). Measured and declared primaries
   * name the SAME semantic (the bone's pointing direction), so mixing across
   * sides is sound; twist secondaries stay same-kind (canonical on both sides).
   * Declared axes carry no measurement noise — a measured side contributes its
   * own. The degradation cause stays NAMED, never hidden.
   */
  const declaredFrame = (
    why: ReorientDegradation,
    srcMeasured: { dir: Vec3Data; noise: number } | null,
    tgtMeasured: { dir: Vec3Data; noise: number } | null,
  ): ReorientFrameMap | null => {
    const pick = (side: ReorientBoneSide, measured: { dir: Vec3Data; noise: number } | null) => {
      if (measured !== null) return measured;
      const dp = side.declaredPrimaryLocal;
      if (dp === null || vec3Length(dp) <= SECONDARY_MIN) return null;
      return { dir: vec3Normalize(dp), noise: 0 };
    };
    const s = pick(input.source, srcMeasured);
    const t = pick(input.target, tgtMeasured);
    if (s === null || t === null) return null;
    const ss = projectedCanonical(input.source, s.dir, s.noise);
    const ts0 = projectedCanonical(input.target, t.dir, t.noise);
    if (ss === null || ts0 === null) return null; // primary-only would leave twist to the prior — not this tier
    return frameFromEvidence(s, t, ss, ts0, 'declared-frame', why);
  };

  /**
   * Authored-frame tier (ADR-010 §11, PR #24) — the Hand Studio frame
   * contract's consumer, ranked ABOVE every measured tier: an authored
   * (family, bone) entry is a human/tool DECLARATION of the bone's local
   * frame, and the hand wins over automation. It FIRES when at least one side
   * carries a validated authored primary+secondary pair (unit + ⊥ gated at
   * priors load). That side contributes its declared frame WHOLESALE (noise 0,
   * never mixed with measured on the same side); a NON-authored side
   * contributes its best existing evidence, mirroring the declared-frame
   * hybrid — the measured child direction where informative and valid, else
   * the declared primary, with the canonical secondary pinning twist. Returns
   * null (ladder proceeds unchanged) when no side is authored, when a
   * non-authored side cannot complete (null primary or secondary), or when the
   * flip axis is degenerate. Authored is AUTHORITY, not degradation: high
   * confidence, degradedBecause null.
   */
  const authoredFrame = (): ReorientFrameMap | null => {
    const authored = (side: ReorientBoneSide): boolean =>
      side.authoredPrimaryLocal !== null && side.authoredSecondaryLocal !== null;
    if (!authored(input.source) && !authored(input.target)) return null;
    const pick = (
      side: ReorientBoneSide,
      measured: { dir: Vec3Data; noise: number } | null,
    ): { primary: { dir: Vec3Data; noise: number }; secondary: Vec3Data } | null => {
      if (authored(side)) {
        // The authored frame wins wholesale for this side — noise 0.
        return {
          primary: { dir: vec3Normalize(side.authoredPrimaryLocal!), noise: 0 },
          secondary: vec3Normalize(side.authoredSecondaryLocal!),
        };
      }
      // Non-authored side: best existing evidence (the declared-frame hybrid).
      let primary: { dir: Vec3Data; noise: number } | null = null;
      if (input.childDirectionInformative && measured !== null) {
        primary = measured;
      } else if (side.declaredPrimaryLocal !== null && vec3Length(side.declaredPrimaryLocal) > SECONDARY_MIN) {
        primary = { dir: vec3Normalize(side.declaredPrimaryLocal), noise: 0 };
      }
      if (primary === null) return null;
      const secondary = projectedCanonical(side, primary.dir, primary.noise);
      if (secondary === null) return null;
      return { primary, secondary };
    };
    const s = pick(input.source, mSrc);
    const t = pick(input.target, mTgt);
    if (s === null || t === null) return null;
    const frame = frameFromEvidence(s.primary, t.primary, s.secondary, t.secondary, 'authored-frame', null);
    if (frame === null) return null; // degenerate flip axis — let the ladder run
    return { ...frame, confidence: 'high', degradedBecause: null };
  };

  // Authored declarations outrank every measured tier (the hand wins, §11) —
  // evaluated before the clavicle branch and the measured happy path.
  const authoredResult = authoredFrame();
  if (authoredResult !== null) return authoredResult;

  if (!input.childDirectionInformative) {
    // The child direction is semantically uninformative here (clavicle
    // class) even when measurable — declared primaries on BOTH sides.
    // (Bilateral needs a mirror-pair of children the clavicle class lacks, so
    // it self-declines here; kept in the chain for any future center bone.)
    return (
      bilateralFrame('child-direction-uninformative', null, null) ??
      declaredFrame('child-direction-uninformative', null, null) ??
      degraded('child-direction-uninformative')
    );
  }
  if (mSrc === null || mTgt === null) {
    const why: ReorientDegradation =
      input.source.childWorldBindPosition === null || input.target.childWorldBindPosition === null
        ? 'missing-child'
        : 'zero-length-child';
    // Bilateral-child (measured per-rig) outranks the hub-idealized declared
    // frame; declared stays the floor when there is no bilateral child pair.
    return bilateralFrame(why, mSrc, mTgt) ?? declaredFrame(why, mSrc, mTgt) ?? degraded(why);
  }

  const ds = mSrc.dir;
  const dt = mTgt.dir;
  const transported = vec3Normalize(quatRotateVec3(bindPrior, dt));
  const residualRadians = Math.acos(clampDot(vec3Dot(transported, ds)));

  // The measured child directions carry angular uncertainty ≈ tolerance/length
  // per side. A pair antiparallel WITHIN that uncertainty must take the
  // evidence-based flip path — the shortest-arc axis there is importer noise,
  // not geometry (review finding, PR #8). Machine ε is only the lower bound:
  // for a gap θ from exact antipodality, 1+dot ≈ θ²/2.
  const angularNoise = mSrc.noise + mTgt.noise;
  const antipodalZone = Math.max(ANTIPODAL_ZONE, (angularNoise * angularNoise) / 2);
  const srcPrimaryNoise = mSrc.noise;
  const tgtPrimaryNoise = mTgt.noise;

  const correction = shortestArc(transported, ds, antipodalZone);
  if (correction !== null) {
    let rotation = quatNormalize(quatMultiply(correction, bindPrior));
    // Twist-evidence ladder: measured grandchild pair (high) → declared
    // canonical-secondary pair (§11, medium) → bind-prior twist (medium).
    // Pairs are never mixed across kinds — a grandchild direction and a
    // canonical axis are different semantic directions.
    const ssG = projectedSecondary(input.source, ds, input.sourceTolerance, srcPrimaryNoise);
    const tsG = projectedSecondary(input.target, dt, input.targetTolerance, tgtPrimaryNoise);
    const ssC = ssG !== null && tsG !== null ? null : projectedCanonical(input.source, ds, srcPrimaryNoise);
    const tsC = ssG !== null && tsG !== null ? null : projectedCanonical(input.target, dt, tgtPrimaryNoise);
    const pair =
      ssG !== null && tsG !== null
        ? { ss: ssG, ts: tsG, confidence: 'high' as const, evidence: 'two-directions' as const }
        : ssC !== null && tsC !== null
          ? { ss: ssC, ts: tsC, confidence: 'medium' as const, evidence: 'canonical-secondary' as const }
          : null;
    if (pair !== null) {
      // Align the twist DOF the primary cannot see: rotate about ds by the
      // signed in-plane angle between the transported and source secondaries.
      const transportedSecondary = vec3Normalize(quatRotateVec3(rotation, pair.ts));
      const twist = Math.atan2(
        vec3Dot(ds, vec3Cross(transportedSecondary, pair.ss)),
        clampDot(vec3Dot(transportedSecondary, pair.ss)),
      );
      rotation = quatNormalize(quatMultiply(quatFromAxisAngle(ds, twist), rotation));
      return {
        kind: 'frameMap',
        frameMap: { kind: 'proper', rotation },
        confidence: pair.confidence,
        evidence: pair.evidence,
        degradedBecause: null,
        residualRadians,
      };
    }
    return {
      kind: 'frameMap',
      frameMap: { kind: 'proper', rotation },
      confidence: 'medium',
      evidence: 'primary-plus-bind-prior',
      degradedBecause: null,
      residualRadians,
    };
  }

  // Anti-parallel (within the derived zone): every π rotation about an axis
  // ⊥ transported maps the primary correctly — the axis must come from
  // evidence, never a hardcoded X/Y/Z (the v2 Rz-vs-Rx flip-family scar).
  // Same evidence ladder as above.
  const flipFromPair = (
    ss: Vec3Data,
    ts0: Vec3Data,
    confidence: 'high' | 'medium',
    evidence: ReorientEvidence,
  ): ReorientResult | null => {
    const ts = vec3Normalize(quatRotateVec3(bindPrior, ts0));
    let axis = vec3Add(ts, ss);
    if (vec3Length(axis) <= SECONDARY_MIN) axis = vec3Cross(transported, ts);
    axis = vec3Sub(axis, vec3Scale(transported, vec3Dot(axis, transported)));
    if (vec3Length(axis) <= SECONDARY_MIN) return null;
    const rotation = quatNormalize(quatMultiply(quatFromAxisAngle(axis, Math.PI), bindPrior));
    return {
      kind: 'frameMap',
      frameMap: { kind: 'proper', rotation },
      confidence,
      evidence,
      degradedBecause: null,
      residualRadians,
    };
  };

  const ssG = projectedSecondary(input.source, ds, input.sourceTolerance, srcPrimaryNoise);
  const tsG = projectedSecondary(input.target, dt, input.targetTolerance, tgtPrimaryNoise);
  if (ssG !== null && tsG !== null) {
    const resolved = flipFromPair(ssG, tsG, 'high', 'two-directions');
    if (resolved !== null) return resolved;
    return {
      kind: 'undetermined',
      reason: 'collinear-secondary',
      needs: 'a non-collinear grandchild/reference direction',
    };
  }
  const ssC = projectedCanonical(input.source, ds, srcPrimaryNoise);
  const tsC = projectedCanonical(input.target, dt, tgtPrimaryNoise);
  if (ssC !== null && tsC !== null) {
    const resolved = flipFromPair(ssC, tsC, 'medium', 'canonical-secondary');
    if (resolved !== null) return resolved;
  }

  // No usable secondaries: the bind prior's own axis is admissible evidence
  // when it has a stable component perpendicular to the flip — this is what
  // preserves the computed Rx-vs-Rz family instead of inventing one.
  const priorVec: Vec3Data = { x: bindPrior.x, y: bindPrior.y, z: bindPrior.z };
  const priorAxis = vec3Sub(priorVec, vec3Scale(transported, vec3Dot(priorVec, transported)));
  if (vec3Length(priorAxis) > SECONDARY_MIN) {
    const rotation = quatNormalize(quatMultiply(quatFromAxisAngle(priorAxis, Math.PI), bindPrior));
    return {
      kind: 'frameMap',
      frameMap: { kind: 'proper', rotation },
      confidence: 'low',
      evidence: 'antipodal-bind-axis',
      degradedBecause: null,
      residualRadians,
    };
  }

  const hadSecondaryData =
    input.source.grandchildWorldBindPosition !== null ||
    input.target.grandchildWorldBindPosition !== null ||
    input.source.canonicalSecondaryLocal !== null ||
    input.target.canonicalSecondaryLocal !== null;
  return {
    kind: 'undetermined',
    reason: hadSecondaryData ? 'collinear-secondary' : 'antiparallel-axis-ambiguous',
    needs: 'a non-collinear secondary direction or a declared twist axis',
  };
}
